import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { roleLevel } from './lib/roles';
import DashboardLayout from './layouts/DashboardLayout';
import LoginPage from './pages/auth/LoginPage';
import SsoConsumePage from './pages/auth/SsoConsumePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ScreenshotsPage from './pages/dashboard/ScreenshotsPage';
import ActivityPage from './pages/dashboard/ActivityPage';
import DevicesPage from './pages/dashboard/DevicesPage';
import TeamPage from './pages/dashboard/TeamPage';
import LogsPage from './pages/dashboard/LogsPage';
import ReleasesPage from './pages/dashboard/ReleasesPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RoleRoute({ minRole, children }: { minRole: string; children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (roleLevel(user?.role) < roleLevel(minRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Not wrapped in PublicRoute/PrivateRoute: this must work whether or
          not a stale session is already present (a new SSO ticket should
          always be able to establish a fresh one), and PublicRoute would
          otherwise redirect an already-authenticated user away before the
          ticket is ever consumed. */}
      <Route path="/sso/consume" element={<SsoConsumePage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <DashboardLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="screenshots"
          element={
            <RoleRoute minRole="admin">
              <ScreenshotsPage />
            </RoleRoute>
          }
        />
        <Route
          path="activity"
          element={
            <RoleRoute minRole="admin">
              <ActivityPage />
            </RoleRoute>
          }
        />
        <Route
          path="devices"
          element={
            <RoleRoute minRole="manager">
              <DevicesPage />
            </RoleRoute>
          }
        />
        <Route
          path="team"
          element={
            <RoleRoute minRole="manager">
              <TeamPage />
            </RoleRoute>
          }
        />
        <Route
          path="logs"
          element={
            <RoleRoute minRole="manager">
              <LogsPage />
            </RoleRoute>
          }
        />
        <Route
          path="releases"
          element={
            <RoleRoute minRole="admin">
              <ReleasesPage />
            </RoleRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
